/**
 * =============================================================================
 * Componente: AffiliateImportStep
 * Versão: 1.0.0
 *
 * Etapa 3 — usuário escolhe o programa de destino e o modo de aprovação,
 * dispara a importação. Mostra progresso enquanto a RPC `create_affiliate`
 * é chamada para cada linha válida e nova.
 * =============================================================================
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Users, UserPlus, UserCheck, Award, AlertTriangle } from 'lucide-react';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { useReferralAccess } from '@/hooks/useReferralAccess';
import { ReferralPaywall } from '@/components/referral/ReferralPaywall';
import { useMemo } from 'react';
import { AffiliateValidationStats, AffiliateImportProgress } from '@/hooks/useAffiliateImport';

interface Props {
  programId: string;
  onProgramChange: (id: string) => void;
  forceApprove: boolean;
  onForceApproveChange: (v: boolean) => void;
  validationStats: AffiliateValidationStats | null;
  isImporting: boolean;
  progress: AffiliateImportProgress | null;
  onStartImport: () => void;
  /**
   * Notifica o wizard pai sobre o estado da etapa para que ele renderize
   * o botão "Iniciar importação" (e eventualmente "Fazer upgrade") no rodapé
   * fixo, mantendo o mesmo padrão visual das demais etapas.
   */
  onReadyChange?: (state: {
    canStart: boolean;
    overQuota: boolean;
    openPaywall: () => void;
  }) => void;
}

export default function AffiliateImportStep({
  programId,
  onProgramChange,
  forceApprove,
  onForceApproveChange,
  validationStats,
  isImporting,
  progress,
  onStartImport,
  onReadyChange,
}: Props) {
  const { data: programs = [], isLoading } = useReferralPrograms();
  const { data: access } = useReferralAccess();
  const activePrograms = useMemo(() => programs.filter((p) => p.is_active), [programs]);
  const [paywallOpen, setPaywallOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Validação de capacidade do plano (afiliados)
  // limit === null  → ilimitado
  // remaining       → quantos novos ainda cabem
  // newAffiliates   → quantos seriam efetivamente criados pelo lote
  // ---------------------------------------------------------------------------
  const affiliatesLimit = access?.limits.affiliates ?? null;
  const affiliatesUsage = access?.usage.affiliates ?? 0;
  const remaining = affiliatesLimit === null ? Infinity : Math.max(0, affiliatesLimit - affiliatesUsage);
  const newAffiliates = validationStats?.newAffiliates ?? 0;
  const overQuota = newAffiliates > remaining;

  const canStart = !!programId && !isImporting && !overQuota;

  // Publica o estado da etapa para o rodapé fixo do wizard renderizar
  // os botões "Fazer upgrade" (quando overQuota) e "Iniciar importação".
  useEffect(() => {
    onReadyChange?.({
      canStart,
      overQuota,
      openPaywall: () => setPaywallOpen(true),
    });
  }, [canStart, overQuota, onReadyChange]);

  if (isImporting && progress) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-6">
        <Loader2 className="h-16 w-16 animate-spin text-primary" />
        <div className="text-center space-y-2">
          <p className="text-lg font-medium">{progress.message}</p>
          <p className="text-sm text-muted-foreground">{progress.current} de {progress.total}</p>
        </div>
        <Progress value={(progress.current / Math.max(progress.total, 1)) * 100} className="w-80" />
        <Alert className="max-w-md">
          <AlertDescription>Não feche esta janela. O processo pode levar alguns minutos.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" /> Programa de destino
          </CardTitle>
          <CardDescription>
            Todos os afiliados deste lote serão criados no programa selecionado.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Programa *</Label>
            <Select value={programId} onValueChange={onProgramChange} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder={isLoading ? 'Carregando...' : 'Selecione o programa'} />
              </SelectTrigger>
              <SelectContent>
                {activePrograms.length === 0 && !isLoading && (
                  <div className="px-2 py-3 text-xs text-muted-foreground">
                    Nenhum programa ativo. Crie um em Indicação → Configurações.
                  </div>
                )}
                {activePrograms.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="space-y-0.5 pr-3">
              <Label className="text-sm">Aprovar imediatamente</Label>
              <p className="text-xs text-muted-foreground">
                Cria os afiliados já com status <strong>ativo</strong>. Desligue para deixar pendente de moderação.
              </p>
            </div>
            <Switch checked={forceApprove} onCheckedChange={onForceApproveChange} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Resumo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <UserPlus className="h-8 w-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold">{validationStats?.newAffiliates || 0}</p>
                <p className="text-sm text-muted-foreground">Novos a criar</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <UserCheck className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{validationStats?.existingInSystem || 0}</p>
                <p className="text-sm text-muted-foreground">Já existentes (ignorados)</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{validationStats?.valid || 0}</p>
                <p className="text-sm text-muted-foreground">Linhas válidas</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Aviso de capacidade do plano (excedida) */}
      {overQuota && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Limite de afiliados do plano excedido</AlertTitle>
          <AlertDescription>
            Seu plano permite mais{' '}
            <strong>{remaining === Infinity ? '∞' : remaining}</strong> afiliado(s) e este lote criaria{' '}
            <strong>{newAffiliates}</strong>. Reduza o CSV ou faça upgrade para continuar.
          </AlertDescription>
        </Alert>
      )}

      {/* Botões "Fazer upgrade" e "Iniciar importação" são renderizados pelo
          rodapé fixo do wizard (ImportAffiliatesWizard), via onReadyChange. */}

      {/* Paywall em modal — reutiliza componente padrão do módulo */}
      <Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
        <DialogContent className="max-w-5xl p-0">
          <ReferralPaywall
            title="Limite de afiliados atingido"
            subtitle={`Seu plano atual permite ${affiliatesLimit ?? '∞'} afiliados. Faça upgrade para importar mais.`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
